<template>
  <div class="files-content">
    <div aria-hidden="true" class="page-glow"></div>
    <div aria-hidden="true" class="page-grid"></div>
    <div class="layout">
      <!-- ============ 顶部信息栏 ============ -->
      <header class="site-header mica">
        <div class="shell">
          <div class="header-row">
            <div class="brand">
              <div class="brand-avatar-wrap">
                <span aria-hidden="true" class="avatar">
                  <img
                    v-if="avatarSrc && !avatarError"
                    :src="avatarSrc"
                    alt=""
                    @error="avatarError = true"
                  />
                  <iconify-icon v-else icon="solar:user-bold"></iconify-icon>
                </span>
                <span
                  v-if="isVip"
                  aria-label="VIP 用户"
                  class="vip-badge"
                  title="VIP 用户"
                >
                  <iconify-icon icon="mdi:crown"></iconify-icon>
                </span>
              </div>
              <div class="brand-text">
                <p class="brand-name">{{ shareTitle }}</p>
              </div>
            </div>
            <!-- 桌面端：二维码 / 举报入口（移动端与底部操作栏互斥，故移动端隐藏） -->
            <div
              v-if="!isMobile && !isExpired"
              class="header-actions relative items-center gap-2"
            >
              <button
                aria-label="显示二维码"
                class="icon-btn-soft"
                title="显示二维码"
                type="button"
                @click="openQr"
              >
                <iconify-icon icon="solar:qr-code-bold"></iconify-icon>
                <span class="btn-label">二维码</span>
              </button>
              <button
                aria-label="举报该分享"
                class="icon-btn-danger"
                title="举报该分享"
                type="button"
                @click="fileReport"
              >
                <iconify-icon icon="solar:danger-triangle-bold"></iconify-icon>
                <span class="btn-label">举报</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- ============ 内容区 ============ -->
      <main class="content-main">
        <div class="shell content-main-inner">
          <div class="share-focus">
            <!-- 正常态：文件预览 + 信息 -->
            <section
              v-if="isLoaded && !isExpired && data.info"
              class="file-preview"
              style="margin: 0 60px"
            >
              <div class="preview-canvas">
                <!-- 图片：点击唤起 Element 图片预览（放大/缩放/切换） -->
                <el-image
                  v-if="data.info.isImage"
                  class="preview-elimage preview-elimage--photo"
                  :src="data.info.wallpaper_h5 || data.info.icon"
                  :preview-src-list="[data.info.wallpaper_web]"
                  fit="cover"
                  @load="onImageLoad"
                ></el-image>
                <!-- 非图片：点击进入文档在线预览 -->
                <el-image
                  v-else
                  class="preview-elimage preview-elimage--icon"
                  :src="data.info.icon"
                  fit="contain"
                  @click.native="previewFile"
                ></el-image>
              </div>
            </section>

            <div
              v-if="isLoaded && !isExpired && data.info"
              class="file-summary"
            >
              <h1 class="file-name">{{ fileName }}</h1>
              <p class="file-sub">一位好友与你分享了这份文件，无需登录即可直接下载。</p>
              <div class="file-meta">
                <span class="chip">
                  <iconify-icon icon="solar:code-file-bold"></iconify-icon>
                  {{ fileTypeLabel }}
                </span>
                <span class="chip">{{ fileSize }}</span>
                <span class="chip is-valid">
                  <iconify-icon icon="solar:hourglass-bold"></iconify-icon>
                  {{ expiredText }}
                </span>
              </div>
              <!-- 桌面端页内下载按钮 -->
              <button
                v-if="!isMobile"
                class="icon-btn-sm"
                style="margin-top: 22px"
                type="button"
                @click="startDownload"
              >
                <iconify-icon icon="solar:download-minimalistic-bold"></iconify-icon>
                <span>下载文件</span>
              </button>
            </div>

            <!-- 过期态 / 异常态 -->
            <section
              v-if="isLoaded && isExpired"
              class="expired-panel"
            >
              <div aria-hidden="true" class="expired-art">
                <iconify-icon icon="solar:danger-circle-bold"></iconify-icon>
              </div>
              <h2 class="expired-title">{{ expiredTitle }}</h2>
              <p class="expired-desc">分享链接已过期或已被取消，请联系分享者重新获取。</p>
            </section>
          </div>
        </div>
      </main>
    </div>

    <!-- ============ 移动端底部操作栏 ============ -->
    <div v-if="isMobile" class="action-bar mica">
      <div class="shell action-bar-inner">
        <div v-if="!isExpired" class="action-bar-row">
          <button
            aria-label="显示二维码"
            class="btn-secondary"
            title="显示二维码"
            type="button"
            @click="openQr"
          >
            <iconify-icon icon="solar:qr-code-bold"></iconify-icon>
          </button>
          <button
            aria-label="举报该分享"
            class="btn-secondary"
            title="举报该分享"
            type="button"
            @click="fileReport"
          >
            <iconify-icon icon="solar:danger-triangle-bold" style="color: var(--c2)"></iconify-icon>
          </button>
          <button
            aria-disabled="false"
            class="btn-primary"
            :class="{ 'is-busy': btnLoad }"
            type="button"
            @click="startDownload"
          >
            <span v-if="btnLoad" aria-hidden="true" class="btn-spinner"></span>
            <iconify-icon v-else icon="solar:download-minimalistic-bold"></iconify-icon>
            <span>{{ btnLoad ? '正在准备…' : '下载' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 轻提示 ============ -->
    <div aria-live="polite" class="toast-wrap">
      <div v-if="toast.visible" class="toast">
        <iconify-icon :icon="toast.icon" :style="{ color: toast.color }"></iconify-icon>
        <span>{{ toast.text }}</span>
      </div>
    </div>

    <!-- ============ 二维码弹框 ============ -->
    <div
      v-if="qrVisible"
      aria-modal="true"
      class="overlay"
      role="dialog"
      @click.self="closeQr"
    >
      <div class="dialog qr-dialog">
        <div class="dialog-head">
          <div>
            <h2>扫码查看这份分享</h2>
            <p class="dialog-sub">用手机扫一扫，即可在移动端下载该文件。</p>
          </div>
          <button aria-label="关闭" class="ghost-btn" type="button" @click="closeQr">
            <iconify-icon icon="solar:close-circle-bold"></iconify-icon>
          </button>
        </div>
        <div class="qr-body">
          <div class="qr-panel" :class="{ 'is-expired': isExpired }">
            <img v-if="qrImg && !isExpired" :src="qrImg" class="qr-code" alt="分享二维码" />
          </div>
          <p class="qr-tip"></p>
        </div>
      </div>
    </div>

    <!-- ============ 举报弹层 ============ -->
    <div
      v-if="showBanType"
      aria-modal="true"
      class="overlay"
      role="dialog"
      @click.self="closeReport"
    >
      <div class="dialog">
        <div class="dialog-head">
          <div>
            <h2>举报该分享</h2>
            <p class="dialog-sub">我们会尽快核实处理，你的举报信息不会被公开。</p>
          </div>
          <button aria-label="关闭" class="ghost-btn" type="button" @click="closeReport">
            <iconify-icon icon="solar:close-circle-bold"></iconify-icon>
          </button>
        </div>
        <div class="dialog-body">
          <p class="field-label">请选择举报原因</p>
          <label
            v-for="item in banTypeList"
            :key="item.ban_type"
            class="report-option"
            :class="{ 'is-checked': selectBan === item.ban_type }"
          >
            <input v-model="selectBan" type="radio" name="reportReason" :value="item.ban_type" />
            <span>{{ item.type_name }}</span>
          </label>
        </div>
        <div class="dialog-foot">
          <button class="btn-ghost" type="button" @click="closeReport">取消</button>
          <button
            class="btn-danger"
            :class="{ 'is-busy': btnLoad }"
            type="button"
            @click="confirmReport"
          >
            提交举报
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  shareFileInfo,
  getBanType,
  submitBan,
  getCdnSignature,
} from "@/api/sharefiles";
import * as util from "../../utils/util";
import * as QR from "../../utils/weppQrcode";

export default {
  name: "shareFilesDetail2",
  data() {
    return {
      isMobile: false,
      id: "",
      data: {},
      info: {},
      user: {},
      error: false,
      errorMessage: "",
      qrImg: "",
      banTypeList: [],
      selectBan: "",
      showBanType: false,
      btnLoad: false,
      qrVisible: false,
      avatarError: false,
      toast: {
        visible: false,
        text: "",
        icon: "solar:check-circle-bold",
        color: "var(--c4)",
      },
    };
  },
  computed: {
    isLoaded() {
      return this.error || (this.info && this.info.expired_day != null);
    },
    // 切换正常 / 过期状态：沿用原有逻辑（expired_day <= 0 或 出错 视为过期）
    isExpired() {
      if (this.error) return true;
      if (
        this.info &&
        this.info.expired_day != null &&
        this.info.expired_day <= 0
      ) {
        return true;
      }
      return false;
    },
    shareTitle() {
      const nick =
        this.data && this.data.user && this.data.user.usernick
          ? this.data.user.usernick
          : "未找到该用户";
      return nick + " 的分享";
    },
    avatarSrc() {
      return this.data && this.data.user && this.data.user.head_pic
        ? this.data.user.head_pic
        : "";
    },
    isVip() {
      const v = this.data && this.data.highest_vip_level;
      return v != null && (v.package_id === 1 || v.package_id === 2);
    },
    fileName() {
      return this.data.info && this.data.info.cloudfile != null
        ? this.data.info.file_name
        : "";
    },
    fileSize() {
      return (this.data.info && this.data.info.Length) || "";
    },
    fileTypeLabel() {
      const ext = this.data.info && this.data.info.Extension;
      return ext ? ext.replace(".", "").toUpperCase() : "";
    },
    expiredText() {
      if (this.info && this.info.expired_day > 0) {
        return this.info.expired_day + "天后过期";
      }
      return "已过期";
    },
    expiredTitle() {
      return this.error
        ? this.errorMessage || "未找到该用户的分享"
        : "该分享已过期";
    },
  },
  mounted() {
    this.checkDeviceType();
    this._onResize = this.debounce(this.checkDeviceType, 200);
    window.addEventListener("resize", this._onResize);
    this.loadIconify();
  },
  created() {
    var url = window.location.href;
    // 使用正则表达式截取目标段落
    var extractedPart = url.match(/\/s\/(.+)/)[1].split("_")[0];
    // 提取截取的部分
    this.id = extractedPart ? extractedPart : this.$route.query.encryption_key;
    if (this.IEVersion() != -1) {
      this.error = true;
      this.errorMessage =
        "当前页面不支持IE11及以下浏览器访问，建议使用Chrome、Edge、Firefox等浏览器访问。";
      return;
    }
    this.getFiles();
  },
  methods: {
    loadIconify() {
      if (
        window.customElements &&
        window.customElements.get("iconify-icon")
      ) {
        return;
      }
      var s = document.createElement("script");
      s.src =
        "https://code.iconify.design/iconify-icon/2.1.0/iconify-icon.min.js";
      s.async = true;
      document.head.appendChild(s);
    },
    checkDeviceType() {
      const width = window.innerWidth;
      this.isMobile = width <= 768;
    },
    debounce(func, delay) {
      let timerId;
      return function () {
        clearTimeout(timerId);
        timerId = setTimeout(() => {
          func.apply(this, arguments);
        }, delay);
      };
    },
    // 举报
    fileReport() {
      getBanType().then((res) => {
        if (res.code == 100) {
          this.banTypeList = res.data;
          this.showBanType = true;
        }
      });
    },
    closeReport() {
      this.showBanType = false;
    },
    confirmReport() {
      if (!this.selectBan) {
        this.$message.error("请先选择举报原因");
        return;
      }
      this.btnLoad = true;
      let data = {
        encryption_key: this.id,
        ban_type: this.selectBan,
      };
      submitBan(data).then((res) => {
        this.btnLoad = false;
        if (res.code == 100) {
          this.showBanType = false;
          this.$message({
            message: "举报成功",
            type: "success",
          });
        } else {
          this.$message({
            message: res.message,
            type: "error",
          });
        }
      });
    },
    onImageLoad() {},
    getFiles() {
      const data_opt = {
        encryption_key: this.id,
      };
      shareFileInfo(data_opt).then((data) => {
        if (data.code === 100 && data.data) {
          var item = data.data;
          item.info.type = util.fileType(
            item.info.file_name.substr(item.info.file_name.lastIndexOf("."))
          );
          item.info.icon = `https://images.hipcapi.com/images/file_icon/new/${util.fileType(
            item.info.file_name.substr(item.info.file_name.lastIndexOf("."))
          )}.svg`;
          item.info.wallpaper_h5 = item.info.cloudfile.callback320
            ? `https://pic-icon.hipcapi.com${item.info.cloudfile.callback320}`
            : ``;
          item.info.wallpaper_web = `https://cloud.hipcapi.com/${item.info.cloudfile.file_key}`;
          item.info.isImage = util.fileTypeImage(
            item.info.file_name.substr(item.info.file_name.lastIndexOf("."))
          );
          item.info.Size = item.info.cloudfile.filesize;
          item.info.Length = util.fs(item.info.cloudfile.filesize);
          item.info.Extension = item.info.file_name.substr(
            item.info.file_name.lastIndexOf(".")
          );

          this.data = item;
          this.info = item.info;
          this.user = item.user;
          document.title = "HiPC分享";
          this.error = false;
          this.qrImg = QR.drawImg(window.location.href, {
            typeNumber: 4,
            errorCorrectLevel: "M",
            size: 150,
          });
        } else {
          this.error = true;
          this.errorMessage = data.message;
        }
      });
    },

    IEVersion() {
      var userAgent = navigator.userAgent;
      var isIE =
        userAgent.indexOf("compatible") > -1 &&
        userAgent.indexOf("MSIE") > -1;
      var isEdge = userAgent.indexOf("Edge") > -1 && !isIE;
      var isIE11 =
        userAgent.indexOf("Trident") > -1 &&
        userAgent.indexOf("rv:11.0") > -1;

      if (isIE) {
        var reIE = new RegExp("MSIE (\\d+\\.\\d+);");
        reIE.test(userAgent);
        var fIEVersion = parseFloat(RegExp["$1"]);
        if (fIEVersion == 7) {
          return 7;
        } else if (fIEVersion == 8) {
          return 8;
        } else if (fIEVersion == 9) {
          return 9;
        } else if (fIEVersion == 10) {
          return 10;
        } else {
          return 6;
        }
      } else if (isEdge) {
        return "edge";
      } else if (isIE11) {
        return 11;
      } else {
        return -1;
      }
    },

    /**
     * 下载文件
     */
    download(filesUrl) {
      var form = document.createElement("a");
      form.href = filesUrl;
      document.body.appendChild(form);
      form.click();
      document.body.removeChild(form);
    },
    /**
     * 生成cnd签名
     */
    downFileApi() {
      let filesUrl = `https://cloud.hipcapi.com/${encodeURIComponent(
        this.info.cloudfile.file_key
      )}`;
      this.download(filesUrl);
    },
    // 预览文件
    previewFile() {
      var fileReg = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)$/;
      var Extension = this.info.Extension;
      if (!fileReg.test(Extension)) {
        this.$message.error("此类型文件暂不支持在线预览");
        return;
      }
      const data_opt = {
        path: this.info.cloudfile.file_key,
      };
      getCdnSignature(data_opt).then((res) => {
        if (res.code === 100) {
          let filesUrl = res.data;
          const reg = /\.(ppt|pptx)$/;
          if (reg.test(Extension)) {
            filesUrl = `https://view.officeapps.live.com/op/view.aspx?src=${filesUrl}`;
          }
          this.goToPageWithParams("PreviewFile", { filesUrl, type: Extension });
        }
      });
    },
    // 带参数跳转到指定路由
    goToPageWithParams(routeName, params) {
      this.$router.push({ name: routeName, params: params });
    },

    /* ---------- 二维码弹框 ---------- */
    openQr() {
      this.qrVisible = true;
    },
    closeQr() {
      this.qrVisible = false;
    },

    /* ---------- 下载（沿用原有下载逻辑，仅增加加载态与提示） ---------- */
    startDownload() {
      if (this.isExpired) {
        this.showToast("分享已过期，暂时无法下载", "error");
        return;
      }
      if (this.btnLoad) return;
      this.btnLoad = true;
      this.showToast("正在准备下载…", "info");
      var self = this;
      setTimeout(function () {
        self.downFileApi();
        self.btnLoad = false;
        self.showToast("下载已开始 · " + self.fileName, "success");
      }, 1400);
    },

    /* ---------- 轻提示 ---------- */
    showToast(message, type) {
      var configs = {
        success: { icon: "solar:check-circle-bold", color: "var(--c4)" },
        error: { icon: "solar:danger-triangle-bold", color: "var(--c2)" },
        info: { icon: "solar:info-circle-bold", color: "var(--c1)" },
      };
      var config = configs[type] || configs.info;
      this.toast = {
        visible: true,
        text: message,
        icon: config.icon,
        color: config.color,
      };
      if (this._toastTimer) {
        window.clearTimeout(this._toastTimer);
      }
      this._toastTimer = window.setTimeout(() => {
        this.toast.visible = false;
      }, 2600);
    },
  },
  beforeDestroy() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
    }
  },
};
</script>

<style>
/* ========== 设计令牌：可见色值 100% 取自色值表 ========== */
:root {
  /* 文字 */
  --t1: #0A1133;
  --t2: #7A8099;
  --t3: #B8BBCC;
  --t4: #FFFFFF;
  /* 边框 / 背景 */
  --l1: #DCDEE5;
  --b2: #F5F8FF;
  --b1: #FFFFFF;
  /* 品牌与状态 */
  --c1: #4D4DFF;
  --c3: #4D6AFF;
  --c2: #FF0055;
  --c4: #00B23B;
  --c5: #FF9500;

  /* ---- 派生色（不在色值表中，按语义就近映射）---- */
  --link: var(--c1);
  --hover: rgba(10, 17, 51, .06);
  --hairline: rgba(10, 17, 51, .08);
  --c1-soft: rgba(77, 77, 255, .10);
  --c1-ring: rgba(77, 77, 255, .30);
  --c1-glow: rgba(77, 77, 255, .28);
  --c2-soft: rgba(255, 0, 85, .10);
  --c2-glow: rgba(255, 0, 85, .28);
  --overlay: rgba(10, 17, 51, .45);
  --disabled-bg: rgba(10, 17, 51, .04);
  --glass-strong: rgba(255, 255, 255, .78);
  --dialog-bg: rgba(255, 255, 255, .93);
  --shadow-1: 0 1px 2px rgba(10, 17, 51, .05), 0 10px 30px rgba(10, 17, 51, .08);
  --shadow-2: 0 18px 48px rgba(10, 17, 51, .16);
  --glow-a: rgba(77, 106, 255, .20);
  --glow-b: rgba(77, 77, 255, .13);
  --gold-a: #FFC53D;
  --gold-b: #FF9F0A;
  --gold-ink: #6B3F00;
  --doc-body: #BBD5FF;
  --doc-fold: #7FA6FF;
  --doc-mark: #FFFFFF;

  /* 圆角尺度（参考 Windows 11 / Edge，全局统一） */
  --r-sm: 10px;
  --r-md: 14px;
  --r-lg: 20px;
  --r-xl: 28px;

  /* 二维码弹框与视觉细节派生色（沿用色值表语义） */
  --qr-card: rgba(255, 255, 255, .72);
  --qr-frame-bg: #FFFFFF;
  --qr-frame-line: rgba(10, 17, 51, .82);
  --sheen: rgba(255, 255, 255, .55);
  --code-line: rgba(10, 17, 51, .30);
  --hairline-strong: rgba(10, 17, 51, .16);
}

@media (prefers-color-scheme: dark) {
  :root {
    --t1: #CFD2E5;
    --t2: #A3AACC;
    --t3: #6B7399;
    --l1: #262D4D;
    --b2: #0A0A1A;
    --b1: #131626;

    --link: #8F95FF;
    --hover: rgba(207, 210, 229, .08);
    --hairline: rgba(207, 210, 229, .10);
    --c1-soft: rgba(77, 77, 255, .20);
    --c1-ring: rgba(77, 77, 255, .42);
    --c1-glow: rgba(77, 77, 255, .32);
    --c2-soft: rgba(255, 0, 85, .14);
    --overlay: rgba(4, 5, 12, .66);
    --disabled-bg: rgba(207, 210, 229, .07);
    --glass-strong: rgba(19, 22, 38, .74);
    --dialog-bg: rgba(19, 22, 38, .94);
    --shadow-1: 0 1px 2px rgba(0, 0, 0, .45), 0 12px 32px rgba(0, 0, 0, .45);
    --shadow-2: 0 22px 56px rgba(0, 0, 0, .62);
    --glow-a: rgba(77, 106, 255, .22);
    --glow-b: rgba(77, 77, 255, .18);
    --doc-body: #243357;
    --doc-fold: #4D6AFF;
    --doc-mark: #D7DDF8;

    --qr-card: rgba(38, 45, 77, .42);
    --qr-frame-bg: #F2F4FF;
    --qr-frame-line: rgba(207, 210, 229, .90);
    --sheen: rgba(255, 255, 255, .10);
    --code-line: rgba(207, 210, 229, .26);
    --hairline-strong: rgba(207, 210, 229, .18);
  }
}

/* ========== 基础 ========== */
* { box-sizing: border-box; }

html.no-scroll { overflow: hidden; }

body {
  font-family: "Segoe UI Variable Text", "Segoe UI", "PingFang SC", "Microsoft YaHei", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
}

.text-t1 { color: var(--t1); }
.text-t2 { color: var(--t2); }
.text-t3 { color: var(--t3); }

.header-actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
}

@media (max-width: 767px) {
  .icon-btn-soft,
  .icon-btn-danger {
    height: 38px;
    padding: 0 12px;
    font-size: 13px;
  }
  .icon-btn-soft iconify-icon,
  .icon-btn-danger iconify-icon { font-size: 16px; }
}

@media (max-width: 480px) {
  .header-actions .btn-label { display: none; }
  .icon-btn-soft,
  .icon-btn-danger { width: 38px; padding: 0; border-radius: 999px; }
}

:focus-visible {
  outline: 2px solid var(--c1);
  outline-offset: 2px;
  border-radius: var(--r-sm);
}

button { font-family: inherit; }

.page-glow {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(680px 420px at 10% -8%, var(--glow-a), transparent 70%),
    radial-gradient(600px 460px at 94% 4%, var(--glow-b), transparent 72%),
    radial-gradient(760px 520px at 46% 120%, var(--glow-b), transparent 72%);
}

.page-grid {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: .22;
  background-image:
    linear-gradient(var(--code-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--code-line) 1px, transparent 1px);
  background-size: 46px 46px;
  -webkit-mask-image: radial-gradient(760px 560px at 50% 22%, #000 0%, transparent 78%);
  mask-image: radial-gradient(760px 560px at 50% 22%, #000 0%, transparent 78%);
}

.shell {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 16px;
}

@media (min-width: 640px) { .shell { padding: 0 24px; } }
@media (min-width: 1024px) { .shell { padding: 0 32px; } }

.layout {
  position: relative;
  z-index: 10;
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

/* ========== 云母 / 亚克力材质（含优雅降级） ========== */
.mica {
  background: var(--glass-strong);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  backdrop-filter: blur(24px) saturate(180%);
}

@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .mica,
  .dialog { background: var(--b1); }
}

/* ========== 顶部信息栏 ========== */
.site-header {
  position: sticky;
  top: 0;
  z-index: 30;
  border-bottom: 1px solid var(--hairline);
}

.header-row {
  display: flex;
  height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

@media (min-width: 768px) { .header-row { height: 76px; } }

.brand {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;
}

.avatar {
  display: flex;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(140deg, var(--c3), var(--c1));
  color: var(--t4);
  font-size: 22px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .28), 0 6px 16px var(--c1-glow);
  overflow: hidden;
}

@media (min-width: 768px) { .avatar { width: 50px; height: 50px; font-size: 24px; } }

.vip-badge {
  position: absolute;
  right: -1px;
  bottom: -1px;
  display: flex;
  width: 19px;
  height: 19px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(140deg, var(--gold-a), var(--gold-b));
  color: var(--gold-ink);
  font-size: 11px;
  box-shadow: 0 0 0 2px var(--b1), 0 2px 6px rgba(0, 0, 0, .18);
}

/* ========== 顶部操作按钮 ========== */
.icon-btn-soft,
.icon-btn-danger {
  display: inline-flex;
  height: 42px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  font-size: 20px;
  cursor: pointer;
  transition: transform .16s ease, background-color .16s ease, filter .16s ease, box-shadow .16s ease;
}

.icon-btn-soft,
.icon-btn-danger {
  gap: 8px;
  padding: 0 16px;
  border: 1px solid var(--l1);
  background: var(--b1);
  color: var(--t1);
  font-size: 14px;
  font-weight: 600;
}

.icon-btn-soft iconify-icon { font-size: 18px; color: var(--t2); }
.icon-btn-soft:hover { transform: translateY(-1px); background: var(--hover); }
.icon-btn-soft:active { transform: translateY(0); }

.icon-btn-danger iconify-icon { font-size: 18px; color: var(--c2); }
.icon-btn-danger:hover { transform: translateY(-1px); border-color: var(--c2); background: var(--c2-soft, var(--hover)); }
.icon-btn-danger:active { transform: translateY(0); }

/* ========== 内容区 ========== */
.content-main { position: relative; z-index: 10; }

.content-main-inner {
  padding-top: 20px;
  padding-bottom: 116px;
}

@media (min-width: 768px) {
  .content-main-inner { padding-top: 40px; padding-bottom: 48px; }
}

/* 单文件聚焦布局：预览居中、信息辅助 */
.share-focus {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

@media (min-width: 768px) { .share-focus { gap: 22px; } }

/* 单文件大图预览：整页视觉焦点，居中呈现 */
.file-preview {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 14px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-xl);
  background: var(--b1);
  box-shadow: var(--shadow-2);
  transition: background-color .25s ease, box-shadow .25s ease;
}

@media (min-width: 768px) { .file-preview { padding: 18px; } }

.preview-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
  background:
    radial-gradient(120% 90% at 12% 0%, var(--glow-a), transparent 62%),
    radial-gradient(120% 90% at 92% 8%, var(--glow-b), transparent 64%),
    linear-gradient(158deg, var(--b2), var(--b1));
}

.preview-canvas svg {
  display: block;
  width: 100%;
  height: 100%;
}

.preview-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin: 12px 0 0;
  padding: 0 4px;
  color: var(--t3);
  font-size: 12px;
}

.preview-strip iconify-icon {
  display: inline-block;
  width: 15px;
  min-width: 15px;
  height: 15px;
  flex: none;
  font-size: 15px;
  line-height: 1;
}

.preview-strip span {
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
  line-height: 1.4;
}

/* 文件信息：紧密贴合预览，作为辅助信息 */
.file-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 720px;
  padding: 0 16px;
  text-align: center;
}

.file-name {
  margin: 0;
  max-width: 100%;
  color: var(--t1);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-all;
}

@media (min-width: 768px) { .file-name { font-size: 22px; } }

.file-sub {
  margin: 0;
  max-width: 560px;
  color: var(--t2);
  font-size: 13.5px;
  line-height: 1.65;
}

@media (min-width: 768px) { .file-sub { font-size: 14px; } }

.file-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid var(--hairline);
  border-radius: 999px;
  background: var(--b2);
  color: var(--t2);
  font-size: 12px;
  font-weight: 500;
}

.chip iconify-icon { font-size: 14px; }

.chip.is-valid { color: var(--c4); border-color: var(--hairline); }
.chip.is-valid iconify-icon { color: var(--c4); }

/* ========== 过期状态区（替代单文件预览）========== */
.expired-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 34px 26px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-xl);
  background: var(--b1);
  box-shadow: var(--shadow-2);
  text-align: center;
  overflow: hidden;
  transition: background-color .25s ease, box-shadow .25s ease;
}

.expired-panel::before {
  content: "";
  position: absolute;
  inset: -40% -20% auto -20%;
  height: 260px;
  background: radial-gradient(60% 100% at 50% 0%, var(--c2-soft), transparent 70%);
  pointer-events: none;
}

@media (min-width: 768px) { .expired-panel { padding: 46px 36px; gap: 16px; } }

.expired-art {
  position: relative;
  display: flex;
  width: 132px;
  height: 132px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(160deg, var(--c2-soft), transparent 78%);
  color: var(--c2);
}

.expired-art iconify-icon { font-size: 52px; }

.expired-art-tag {
  position: absolute;
  right: -4px;
  bottom: 4px;
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--b1);
  border-radius: 999px;
  background: var(--c2);
  color: var(--t4);
  font-size: 19px;
  box-shadow: 0 6px 16px var(--c2-glow);
}

.expired-title {
  margin: 0;
  color: var(--t1);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
}

@media (min-width: 768px) { .expired-title { font-size: 24px; } }

.expired-desc {
  margin: 0;
  max-width: 460px;
  color: var(--t2);
  font-size: 13.5px;
  line-height: 1.7;
}

@media (min-width: 768px) { .expired-desc { font-size: 14px; } }

.expired-fallback {
  margin: 2px 0 0;
  color: var(--t3);
  font-size: 12.5px;
  line-height: 1.6;
}

/* 二维码失效蒙层 */
.qr-panel.is-expired .qr-code { filter: grayscale(1) opacity(.4); }

.qr-panel.is-expired::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  background: var(--overlay);
}

.qr-expired-mask {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--t4);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  padding: 12px;
}

.qr-expired-mask iconify-icon { font-size: 30px; color: var(--c2); }

/* PC 侧下载按钮 */
.icon-btn-sm {
  display: inline-flex;
  min-width: 220px;
  height: 54px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 40px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--c3), var(--c1));
  color: var(--t4);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  cursor: pointer;
  box-shadow: 0 12px 30px var(--c1-glow), 0 2px 8px rgba(77, 77, 255, .25);
  transition: transform .16s ease, filter .16s ease, box-shadow .16s ease;
}

.icon-btn-sm iconify-icon { font-size: 22px; }
.icon-btn-sm:hover {
  transform: translateY(-2px);
  filter: brightness(1.08);
  box-shadow: 0 18px 38px var(--c1-glow), 0 4px 10px rgba(77, 77, 255, .3);
}
.icon-btn-sm:active { transform: translateY(0); }

/* ========== 移动端底部操作栏 ========== */
.action-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  border-top: 1px solid var(--hairline);
}

.action-bar-inner {
  padding-top: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}

@media (min-width: 768px) {
  .action-bar { display: none; }
}

.action-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-secondary {
  display: inline-flex;
  width: 50px;
  height: 50px;
  flex: none;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--l1);
  border-radius: var(--r-md);
  background: var(--b1);
  color: var(--t1);
  font-size: 22px;
  cursor: pointer;
  transition: background-color .14s ease, transform .14s ease;
}

.btn-secondary:hover { background: var(--hover); }
.btn-secondary:active { transform: translateY(1px); }

.btn-primary {
  display: inline-flex;
  width: 100%;
  flex: 1;
  height: 50px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: var(--r-md);
  background: var(--c1);
  color: var(--t4);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 20px var(--c1-glow);
  transition: transform .16s ease, filter .16s ease, opacity .16s ease;
}

.btn-primary iconify-icon { font-size: 20px; }
.btn-primary:hover { filter: brightness(1.07); }
.btn-primary:active { transform: translateY(1px); }
.btn-primary.is-busy { opacity: .88; pointer-events: none; }

.btn-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, .38);
  border-top-color: var(--t4);
  border-radius: 999px;
  animation: spin .8s linear infinite;
}

/* ========== 轻提示 ========== */
.toast-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 96px;
  z-index: 70;
  display: flex;
  justify-content: center;
  padding: 0 16px;
  pointer-events: none;
}

@media (min-width: 768px) { .toast-wrap { bottom: 36px; } }

.toast {
  display: flex;
  max-width: 420px;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-md);
  background: var(--dialog-bg);
  box-shadow: var(--shadow-2);
  color: var(--t1);
  font-size: 14px;
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  backdrop-filter: blur(20px) saturate(180%);
  animation: pop-in .18s ease-out;
}

.toast iconify-icon { flex: none; font-size: 18px; }

/* ========== 二维码弹框 ========== */
.qr-dialog { max-width: 400px; }

.qr-body {
  padding: 4px 20px 20px;
  text-align: center;
}

.qr-panel {
  position: relative;
  margin: 0 auto;
  width: 220px;
  max-width: 100%;
  padding: 18px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
  background: var(--qr-card);
  box-shadow: inset 0 1px 0 var(--sheen);
}

.qr-panel::after {
  content: "";
  position: absolute;
  inset: auto 16px -9px;
  height: 18px;
  border-radius: 999px;
  background: var(--c1-glow);
  filter: blur(14px);
  opacity: .7;
  z-index: -1;
}

.qr-code {
  display: block;
  width: 100%;
  height: auto;
  border-radius: var(--r-sm);
  background: var(--qr-frame-bg);
}

.qr-tip {
  margin: 14px 0 0;
  color: var(--t3);
  font-size: 12.5px;
  line-height: 1.6;
}

/* ========== 举报弹层 ========== */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--overlay);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  animation: fade-in .16s ease-out;
}

.dialog {
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
  background: var(--dialog-bg);
  box-shadow: var(--shadow-2);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  backdrop-filter: blur(24px) saturate(180%);
  animation: pop-in .2s ease-out;
}

.dialog-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
}

.dialog-head h2 {
  margin: 0;
  color: var(--t1);
  font-size: 17px;
  font-weight: 600;
}

.dialog-sub {
  margin: 6px 0 0;
  color: var(--t2);
  font-size: 13px;
}

.ghost-btn {
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex: none;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--t2);
  font-size: 22px;
  cursor: pointer;
  transition: background-color .14s ease, color .14s ease;
}

.ghost-btn:hover { background: var(--hover); color: var(--t1); }

.dialog-body {
  padding: 0 20px;
  overflow-y: auto;
  scrollbar-width: none;
}

.dialog-body::-webkit-scrollbar { display: none; }

.field-label {
  display: block;
  margin: 14px 0 8px;
  color: var(--t2);
  font-size: 13px;
}

.report-option {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding: 12px 14px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-sm);
  color: var(--t1);
  font-size: 14px;
  cursor: pointer;
  transition: border-color .14s ease, background-color .14s ease;
}

.report-option:hover { background: var(--hover); }

.report-option.is-checked {
  border-color: var(--c1);
  background: var(--c1-soft);
}

.report-option input {
  width: 16px;
  height: 16px;
  flex: none;
  margin: 0;
  accent-color: var(--c1);
}

.dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px 20px;
}

.btn-ghost {
  padding: 9px 18px;
  border: 1px solid var(--l1);
  border-radius: var(--r-sm);
  background: transparent;
  color: var(--t1);
  font-size: 14px;
  cursor: pointer;
  transition: background-color .14s ease;
}

.btn-ghost:hover { background: var(--hover); }

.btn-danger {
  padding: 9px 18px;
  border: none;
  border-radius: var(--r-sm);
  background: var(--c2);
  color: var(--t4);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 16px var(--c2-glow);
  transition: transform .14s ease, filter .14s ease;
}

.btn-danger:hover { filter: brightness(1.08); }
.btn-danger:active { transform: translateY(1px); }
.btn-danger.is-busy { opacity: .88; pointer-events: none; }

/* ========== 动效 ========== */
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes pop-in {
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  * { animation-duration: .001ms !important; transition-duration: .001ms !important; }
}

/* ========== Vue 组件补充 ========== */
.files-content {
  position: relative;
  min-height: 100vh;
  font-family: "Segoe UI Variable Text", "Segoe UI", "PingFang SC", "Microsoft YaHei", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: var(--t1);
  background: var(--b2);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.brand-avatar-wrap {
  position: relative;
  flex: none;
}

.avatar img {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  display: block;
}

.brand-text {
  min-width: 0;
}

.brand-name {
  margin: 0;
  color: var(--t1);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: .01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-elimage--photo {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--r-lg);
}

.preview-elimage--photo .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-elimage--icon {
  display: block;
  width: 120px;
  height: 120px;
  cursor: pointer;
}

.preview-elimage--icon .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@media (min-width: 768px) {
  .preview-elimage--icon { width: 150px; height: 150px; }
}
</style>



// WEBPACK FOOTER //
// src/view/hipc/ShareFilesDetail2.vue